<template>
  <v-container v-if="fund" fluid>
    <v-app-bar app color="secondary" dark dense>
      <v-toolbar-title>
        {{ fund.name }}
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-btn
        :to="{
          name: 'auditAuditPeriod',
          params: { id: fund.auditPeriod.id },
        }"
      >
        {{ $t("auditor.minimalRequirements.back") }}
      </v-btn>
    </v-app-bar>

    <MinimalSuccess v-if="awaitingEdit" :fund="fund" />

    <template v-else>
      <v-row>
        <v-col cols="12">
          <AuditStatus :requirements="fund.minimalRequirements" />
        </v-col>

        <v-col cols="12">
          <DataTable :fund="fund" />
        </v-col>
      </v-row>

      <v-row v-for="(category, index) in categories" :key="index">
        <v-col cols="12">
          <h3>
            {{ $t(`minimalRequirementCategory.${index}.name`) }}
          </h3>
        </v-col>

        <v-col v-for="requirement in category" :key="requirement.id" cols="12">
          <MinimalRequirementForm :requirement="requirement" :fund="fund" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" v-if="showResultForm">
          <MinimalRequirementsResultForm :fund="fund" />
        </v-col>

        <v-col>
          <v-dialog v-model="dialog" persistent max-width="600">
            <template #activator="{ on, attrs }">
              <v-btn
                v-if="showFinishButton"
                color="primary"
                class="mr-4"
                :disabled="!readyToFinish"
                v-bind="attrs"
                v-on="on"
              >
                {{ $t("auditor.minimalRequirements.finish") }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="headline">
                {{
                  fund.precheck
                    ? $t("auditor.precheck.confirmation.title")
                    : $t("auditor.minimalRequirements.confirmation.title")
                }}
              </v-card-title>
              <v-card-text>
                <Markdown
                  :value="
                    fund.precheck
                      ? $t('auditor.precheck.confirmation.description')
                      : $t('auditor.minimalRequirements.confirmation.description')
                  "
                />
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="warning" @click="dialog = false">
                  {{
                    fund.precheck
                      ? $t("auditor.precheck.confirmation.cancel")
                      : $t("auditor.minimalRequirements.confirmation.cancel")
                  }}
                </v-btn>
                <v-btn color="primary" @click="finish" :loading="loading">
                  {{
                    fund.precheck
                      ? $t("auditor.precheck.confirmation.accept")
                      : $t("auditor.minimalRequirements.confirmation.accept")
                  }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { all, includes, any } from "ramda";

import AuditStatus from "@/components/Audit/Fund/AuditStatus";
import DataTable from "@/components/Fund/DataTable";
import groupByCategory from "@/utils/Requirements/groupByCategory";
import MinimalSuccess from "@/components/Audit/Fund/MinimalSuccess";
import MinimalRequirementsResultForm from "./MinimalRequirementsResultForm";
import MinimalRequirementForm from "@/components/Audit/Fund/MinimalRequirementForm";
import FundMinimalRequirementsQuery from "@/gql/queries/FundMinimalRequirementsQuery.gql";
import FinishAuditMinimalRequirementsMutation from "@/gql/mutations/FinishAuditMinimalRequirementsMutation.gql";

export default {
  components: {
    DataTable,
    MinimalRequirementForm,
    MinimalSuccess,
    AuditStatus,
    MinimalRequirementsResultForm,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      fund: null,
      dialog: false,
      loading: false,
    };
  },

  computed: {
    categories() {
      return groupByCategory(this.fund.minimalRequirements);
    },

    awaitingEdit() {
      return (
        this.fund.state === "IN_PROGRESS" && includes(this.fund.minimalRequirementsState, ["INITIAL_EDIT", "EDIT"])
      );
    },

    readyToFinish() {
      return all((req) => includes(req.state, ["DONE", "EDIT"]) && !req.wip, this.fund.minimalRequirements);
    },

    showFinishButton() {
      return this.$auth.may(["audit:finish"]) && includes(this.fund.minimalRequirementsState, ["AUDIT"]);
    },

    showResultForm() {
      if (this.fund.precheck) return true;

      return any((req) => req.result.result === "FAIL", this.fund.minimalRequirements);
    },
  },

  apollo: {
    fund: {
      query: FundMinimalRequirementsQuery,
      fetchPolicy: "cache-and-network",
      variables() {
        return {
          id: this.id,
        };
      },
    },
  },

  methods: {
    async finish() {
      this.loading = true;

      await this.$apollo.mutate({
        mutation: FinishAuditMinimalRequirementsMutation,
        variables: {
          id: this.fund.id,
        },
      });

      this.dialog = false;
      this.loading = false;

      this.$router.push({
        name: "auditAuditPeriod",
        params: { id: this.fund.auditPeriod.id },
      });
    },
  },
};
</script>
